import { ErrorModal } from "@/components/shared/modal/error-modal";
import Modal from "@/components/shared/modal/modal";
import {
	Button,
	CircularProgress,
	Grid,
	Snackbar,
	Typography,
} from "@material-ui/core";
import { Alert, Color } from "@material-ui/lab";
import React, { useEffect, useState } from "react";
import { Vendor } from "../../models/Vendor";
import {
	deleteVendor,
	getVendorById,
} from "../../repositories/VendorsRepository";

type Props = {
	show: boolean;
	onHide: (shouldUpdate: boolean) => void;
	vendorIdForDelete: number | null;
};

export const DeleteVendor = ({ show, onHide, vendorIdForDelete }: Props) => {
	const [openAlertMessage, setOpenAlertMessage] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [message, setMessage] = useState("");
	const [messageType, setMessageType] = useState<Color | undefined>(undefined);

	const [vendorForDelete, setVendorForDelete] = useState<Vendor | null>(null);

	const [error, setError] = useState<string | null>(null);
	const [showErrorModal, setShowErrorModal] = useState(false);

	const handleClose = (event?: React.SyntheticEvent, reason?: string) => {
		if (reason === "clickaway") {
			return;
		}
		setOpenAlertMessage(false);
	};

	const handleDelete = async () => {
		setIsSubmitLoading(true);

		const res = await deleteVendor(vendorIdForDelete);
		try {
			if (res.ok) {
				setMessage(" The vendor was deleted successfully");
				setMessageType("success");
				onHide(true);
				setVendorForDelete(null);
			}
		} catch (e) {
			setMessage("Error, please try again");
			setMessageType("error");

			setError(e as string);
			setShowErrorModal(true);
		}
		setIsSubmitLoading(false);
		setOpenAlertMessage(true);
	};

	useEffect(() => {
		if (show) {
			(async () => {
				setIsLoading(true);
				const { vendor } = await getVendorById(vendorIdForDelete);

				setVendorForDelete(vendor?.result);

				setIsLoading(false);
			})();
		}
	}, [show]);

	return (
		<>
			<>
				<Modal
					disableEnforcedFocus={false}
					open={show}
					handleClose={() => {
						onHide(true);
					}}
					id="deleteVendor"
					title={"Delete a vendor"}
					maxWidth="sm"
				>
					<Modal.Content>
						{isLoading && (
							<Grid
								container
								justifyContent="center"
								alignItems="center"
								style={{ minHeight: "140px" }}
							>
								{/* <LazyLoading /> */}
								<CircularProgress size="3rem" />
							</Grid>
						)}

						{!isLoading && (
							<>
								<Grid container spacing={1}>
									<Typography>
										You will not be able to recover deleted data
									</Typography>
								</Grid>
								<br />
								<Grid container spacing={1}>
									<Typography>
										Are you sure you want to delete{" "}
										<strong>{vendorForDelete?.vendorName}</strong>?
									</Typography>
								</Grid>
							</>
						)}
					</Modal.Content>
					{!isLoading && (
						<Modal.Actions style={{ padding: "20px 30px 30px" }}>
							<Grid container spacing={2} justifyContent="flex-end">
								<Grid item md={3} xs={12} style={{ margin: 0 }}>
									<Button
										fullWidth
										variant="contained"
										color="default"
										onClick={() => {
											onHide(false);
											setVendorForDelete(null);
										}}
									>
										Cancel
									</Button>
								</Grid>
								<Grid item md={3} xs={12} style={{ margin: 0 }}>
									<Button
										fullWidth
										startIcon={
											isSubmitLoading ? <CircularProgress size="1rem" /> : <></>
										}
										disabled={isSubmitLoading}
										variant="contained"
										color="secondary"
										onClick={handleDelete}
									>
										Delete
									</Button>
								</Grid>
							</Grid>
						</Modal.Actions>
					)}
				</Modal>
				<Snackbar
					open={openAlertMessage}
					autoHideDuration={2500}
					onClose={handleClose}
				>
					<Alert severity={messageType} onClose={handleClose}>
						{message}
					</Alert>
				</Snackbar>
			</>
			<ErrorModal onHide={() => setError(null)} error={error} />
		</>
	);
};
